import React, { useState, useRef, useEffect } from 'react';
import { useGoogleSearch } from '../../hooks/useGoogleSearch';
import {
  FaSearch as Search,
  FaMapMarkerAlt as MapPin,
  FaTimes as X,
  FaSpinner as Loader2,
} from 'react-icons/fa';

const GooglePlacesAutocomplete = ({
  value = '',
  onChange = () => {},
  onSelect = () => {},
  placeholder = 'Search for a place...',
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  disabled = false,
  required = false,
  error = null,
  label = null,
  helpText = null,
  showClearButton = true,
  minLength = 4,
  debounceDelay = 300,
  apiEndpoint = '/api/location/search_address',
  city = null,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  const {
    query,
    setQuery,
    setSelectedText,
    results,
    loading,
    error: searchError,
    hasSearched,
    canSearch,
    getResultText,
    getPlaceId
  } = useGoogleSearch({
    minLength,
    debounceDelay,
    apiEndpoint,
    city
  });

  // Follow the parent's value when it changes from outside (a picked place's
  // full address, a reset to empty, an edited booking's saved address).
  // Those are not typed searches, so they must not trigger one.
  const queryRef = useRef(query);
  queryRef.current = query;
  useEffect(() => {
    const next = value ?? '';
    if (next !== queryRef.current) setSelectedText(next);
  }, [value, setSelectedText]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange(newValue);

    if (newValue.length >= minLength) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }

    setFocusedIndex(-1);
  };

  const handleSelect = (result) => {
    const resultText = getResultText(result);
    setSelectedText(resultText);
    onChange(resultText);
    onSelect(result);
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const handleClear = () => {
    setQuery('');
    onChange('');
    onSelect(null);
    setIsOpen(false);
    setFocusedIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev =>
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < results.length) {
          handleSelect(results[focusedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const focusedItem = listRef.current.children[focusedIndex];
      if (focusedItem) {
        focusedItem.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [focusedIndex]);

  const baseInputClasses = `
    w-full px-3 py-2 pr-10 text-sm border rounded-lg transition-colors
    bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100
    placeholder-slate-400 dark:placeholder-slate-500
    focus:outline-none focus:ring-2 focus:ring-emerald-500/30 dark:focus:ring-emerald-400/30 focus:border-emerald-500 dark:focus:border-emerald-400
    disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:cursor-not-allowed
    ${error ? 'border-red-500 dark:border-red-500 focus:ring-red-500/30 focus:border-red-500' : 'border-slate-200 dark:border-slate-600'}
    ${inputClassName}
  `;

  return (
    <div className={`relative ${className}`}>
      {}
      {label && (
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
          {label}
          {required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
        </label>
      )}

      {}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-slate-400 dark:text-slate-500" />
        </div>

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (canSearch && results.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={baseInputClasses}
          style={{ paddingLeft: '2.5rem' }}
          {...props}
        />

        {}
        {showClearButton && query && !disabled && !props.readOnly && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {}
        {loading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <Loader2 className="h-4 w-4 animate-spin text-emerald-500 dark:text-emerald-400" />
          </div>
        )}
      </div>

      {}
      {isOpen && (
        <div
          ref={dropdownRef}
          className={`absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-card-hover max-h-60 overflow-y-auto ${dropdownClassName}`}
        >
          {loading ? (
            <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400 flex items-center">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Searching...
            </div>
          ) : searchError ? (
            <div className="px-3 py-2 text-sm text-red-500 dark:text-red-400">
              {searchError}
            </div>
          ) : results.length === 0 && hasSearched ? (
            <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
              No places found
            </div>
          ) : results.length === 0 && !hasSearched ? (
            <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
              {canSearch ? 'Type to search...' : `Type at least ${minLength} characters...`}
            </div>
          ) : (
            <ul ref={listRef} className="py-1">
              {results.map((result, index) => (
                <li
                  key={getPlaceId(result) || index}
                  className={`px-3 py-2 text-sm cursor-pointer flex items-start hover:bg-slate-100 dark:hover:bg-slate-700 ${
                    index === focusedIndex ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'text-slate-900 dark:text-slate-100'
                  }`}
                  onClick={() => handleSelect(result)}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  <MapPin className="h-4 w-4 text-slate-400 dark:text-slate-500 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="flex-1 min-w-0" title={getResultText(result)}>
                    <div className="font-medium break-words">
                      {result.structured_formatting?.main_text || result.name || getResultText(result) || 'Unknown place'}
                    </div>
                    {result.structured_formatting?.secondary_text && (
                      <div className="text-slate-500 dark:text-slate-400 text-xs break-words">
                        {result.structured_formatting.secondary_text}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {}
      {helpText && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helpText}</p>
      )}

      {}
      {error && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400">{error}</p>
      )}

      {}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-1 text-xs text-slate-400 dark:text-slate-500">
          {canSearch ? 'Ready to search' : `Need ${minLength - query.length} more characters`}
          {hasSearched && ` • ${results.length} results`}
        </div>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;
